import React, { useEffect, useMemo, useState } from "react";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import { loadCatalog, saveCatalog } from "../../data/catalog";
import { loadSuppliers, saveSupplier, deleteSupplier } from "../../data/suppliers";
import {
  loadPurchases,
  savePurchase,
  deletePurchase,
  receivePurchaseItems,
  purchaseStatusOptions,
} from "../../data/purchases";

import SupplierList from "../../components/Suppliers/SupplierList";
import PurchaseOrderList from "../../components/Suppliers/PurchaseOrderList";
import PurchaseOrderForm from "../../components/Suppliers/PurchaseOrderForm";
import StockAlertsPanel from "../../components/Suppliers/StockAlertsPanel";
import SupplierInvoiceTab from "../../components/Suppliers/SupplierInvoiceTab";

const emptyPurchaseItem = { productId: "", productName: "", quantityOrdered: "", quantityReceived: 0, unitCost: "" };

const MyAccountSuppliers = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [activeTab, setActiveTab] = useState("suppliers");
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [purchaseForm, setPurchaseForm] = useState({ supplierId: "", notes: "", expectedDate: "", items: [{ ...emptyPurchaseItem }] });
  const [editingPurchaseId, setEditingPurchaseId] = useState(null);
  const [receiveForm, setReceiveForm] = useState({});
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [initialInvoiceFromPurchase, setInitialInvoiceFromPurchase] = useState(null);

  useEffect(() => {
    let isMounted = true;
    Promise.all([loadSuppliers(), loadPurchases(), loadCatalog()]).then(([s, p, c]) => {
      if (!isMounted) return;
      setSuppliers(s);
      setPurchases(p);
      setCatalogProducts(c);
      setLoaderStatus(false);
    });
    return () => { isMounted = false; };
  }, []);

  const purchaseStats = useMemo(() => {
    const draft = purchases.filter((p) => p.status === "draft").length;
    const ordered = purchases.filter((p) => p.status === "ordered").length;
    const partial = purchases.filter((p) => p.status === "partial").length;
    const received = purchases.filter((p) => p.status === "received").length;
    const totalSpent = purchases.filter((p) => p.status !== "cancelled").reduce((sum, p) => sum + Number(p.totalAmount || 0), 0);
    return { draft, ordered, partial, received, totalSpent };
  }, [purchases]);

  const handleEditPurchase = (purchase) => {
    setPurchaseForm({
        supplierId: purchase.supplierId,
        notes: purchase.notes || "",
        expectedDate: purchase.expectedDate || "",
        items: purchase.items.map(i => ({ ...i }))
    });
    setEditingPurchaseId(purchase.id);
    setReceiveForm({});
    setActiveTab("new-purchase");
  };

  const handleReceivePurchase = (purchase) => {
    const rForm = {};
    purchase.items.forEach((item) => { rForm[item.id] = String(Math.max(0, Number(item.quantityOrdered || 0) - Number(item.quantityReceived || 0))); });
    setReceiveForm(rForm);
    setPurchaseForm({
        supplierId: purchase.supplierId,
        notes: purchase.notes || "",
        expectedDate: purchase.expectedDate || "",
        items: purchase.items.map(i => ({ ...i }))
    });
    setEditingPurchaseId(purchase.id);
    setActiveTab("new-purchase");
  };

  const handleCreateInvoice = (invoiceObj) => {
      setInitialInvoiceFromPurchase(invoiceObj);
      setActiveTab("factures");
  }

  const handleDoneForm = () => {
      setActiveTab("purchases");
  }

  if (loaderStatus) {
    return (
      <div><ScrollToTop /><section><div className="container"><div className="row"><AdminSidebar activePath="/MyAccountSuppliers" /><div className="col-lg-9 col-md-8 col-12"><div className="p-6 p-lg-10"><div className="alert alert-light border mb-0">Chargement...</div></div></div></div></div></section></div>
    );
  }

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAccountSuppliers" />
            <div className="col-lg-9 col-md-8 col-12">
              <div className="p-6 p-lg-10">
                <div className="d-flex justify-content-between align-items-center mb-6 flex-wrap gap-2">
                  <div>
                    <h2 className="mb-1">Fournisseurs & Approvisionnement</h2>
                    <p className="text-muted mb-0">Gérez vos fournisseurs, commandes, alertes stock et factures.</p>
                  </div>
                </div>

                <div className="admin-tabs">
                  {[
                    { key: "suppliers", label: "Fournisseurs" },
                    { key: "purchases", label: "Commandes" },
                    { key: "new-purchase", label: editingPurchaseId ? "Modifier / Recevoir" : "Nouvelle commande" },
                    { key: "alerts", label: "🔔 Alertes Stock" },
                    { key: "factures", label: "📄 Factures" }
                  ].map((tab) => (
                    <button key={tab.key} type="button" className={`admin-tab-btn${activeTab === tab.key ? " active" : ""}`} onClick={() => setActiveTab(tab.key)}>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {message && <div className="alert alert-info py-2 mt-3">{message}</div>}

                {activeTab === "suppliers" && (
                  <SupplierList 
                    suppliers={suppliers} 
                    setSuppliers={setSuppliers}
                    purchaseStats={purchaseStats}
                    search={search}
                    setSearch={setSearch}
                    message={message}
                    setMessage={setMessage}
                  />
                )}

                {activeTab === "purchases" && (
                  <PurchaseOrderList 
                    purchases={purchases}
                    setPurchases={setPurchases}
                    suppliers={suppliers}
                    catalogProducts={catalogProducts}
                    setCatalogProducts={setCatalogProducts}
                    purchaseStats={purchaseStats}
                    search={search}
                    setSearch={setSearch}
                    message={message}
                    setMessage={setMessage}
                    onEditPurchase={handleEditPurchase}
                    onReceivePurchase={handleReceivePurchase}
                    onCreateInvoice={handleCreateInvoice}
                    onNewPurchase={() => {
                        setEditingPurchaseId(null);
                        setPurchaseForm({ supplierId: "", notes: "", expectedDate: "", items: [{ ...emptyPurchaseItem }] });
                        setActiveTab("new-purchase");
                    }}
                  />
                )}

                {activeTab === "new-purchase" && (
                  <PurchaseOrderForm 
                    suppliers={suppliers}
                    catalogProducts={catalogProducts}
                    purchases={purchases}
                    setPurchases={setPurchases}
                    editingPurchaseId={editingPurchaseId}
                    setEditingPurchaseId={setEditingPurchaseId}
                    receiveForm={receiveForm}
                    setReceiveForm={setReceiveForm}
                    message={message}
                    setMessage={setMessage}
                    purchaseForm={purchaseForm}
                    setPurchaseForm={setPurchaseForm}
                    onDone={handleDoneForm}
                  />
                )}

                {activeTab === "alerts" && (
                    <StockAlertsPanel 
                      catalogProducts={catalogProducts}
                      suppliers={suppliers}
                      purchases={purchases}
                      setPurchases={setPurchases}
                      setCatalogProducts={setCatalogProducts}
                      message={message}
                      setMessage={setMessage}
                      onSwitchTab={setActiveTab}
                    />
                )}

                {activeTab === "factures" && (
                    <SupplierInvoiceTab 
                      suppliers={suppliers}
                      catalogProducts={catalogProducts}
                      message={message}
                      setMessage={setMessage}
                      initialInvoiceFromPurchase={initialInvoiceFromPurchase}
                      clearInitialInvoice={() => setInitialInvoiceFromPurchase(null)}
                    />
                )}

              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAccountSuppliers;

